import Select from "react-select";
import type { HandleSelectChange, SelectValues } from "ui-kit/src/MultipleSelect/types";

interface SelectProps {
	items?: { value: string; label: string }[];
	placeholder?: string;
	value?: SelectValues | null;
	handleChange?: HandleSelectChange;
	isCleareable?: boolean;
	menuPortalTarget?: boolean;
	/**
	 * When true, the dropdown is at least as wide as the control and up to ~360px (capped to viewport) so long labels
	 * are visible; options and selected tags wrap. When false, the menu matches the control width (default react-select).
	 */
	wideMenu?: boolean;
}

export default function MultiSelectComponent({
	value,
	placeholder,
	items,
	handleChange,
	isCleareable = true,
	menuPortalTarget,
	wideMenu = false,
}: SelectProps) {
	const customStyles = {
		//@ts-ignore
		container: (provided) => ({
			...provided,
			width: "100%",
		}),
		// @ts-ignore — menuPortal callback is untyped on this inline styles object; z-index must sit above modals when menuPortalTarget is set
		menuPortal: (base) => ({ ...base, zIndex: 10200 }),
		// @ts-ignore
		menu: (provided) => ({
			...provided,
			borderRadius: "5px",
			overflow: "hidden",
			zIndex: 99999,
			...(wideMenu
				? {
					// Wider than a narrow control so list options show full name/email, not a single-line ellipsis.
					minWidth: "max(100%, min(360px, calc(100vw - 32px)))",
				}
				: {}),
		}),
		// @ts-ignore
		option: (provided, state) => ({
			...provided,
			...(wideMenu
				? {
					whiteSpace: "normal" as const,
					wordBreak: "break-word" as const,
				}
				: {}),
			cursor: "pointer",
			":hover": {
				backgroundColor: "#000937",
				color: "#FFFFFF",
			},
			backgroundColor: state.isFocused ? "#000937" : provided.backgroundColor,
			color: state.isFocused ? "#FFFFFF" : provided.color,
		}),
		// @ts-ignore — react-select style typings don't support dynamic keys via spread
		...(wideMenu
			? {
				// @ts-ignore — react-select style typings don't support dynamic keys via spread
				multiValueLabel: (provided) => ({
					...provided,
					whiteSpace: "normal" as const,
					wordBreak: "break-word" as const,
				}),
			}
			: {}),
		//@ts-ignore
		control: (provided) => ({
			...provided,
			borderRadius: "20px",
			cursor: "pointer",
		}),
		//@ts-ignore
		placeholder: (provided) => ({
			...provided,
			textTransform: "uppercase",
			fontSize: "12px",
			color: "#000937",
		}),
	};
	return (
		<Select
			menuPortalTarget={menuPortalTarget ? document.body : null}
			// A portalled menu is positioned from the control's viewport rectangle, which only lines up
			// with the control if the menu is placed in viewport coordinates too. Left absolute, the menu
			// is offset by the page's scroll on pages wide enough to scroll sideways.
			menuPosition={menuPortalTarget ? "fixed" : "absolute"}
			className="react-select"
			classNamePrefix="react-select"
			value={value}
			isMulti
			isClearable={isCleareable}
			styles={customStyles}
			onChange={handleChange}
			closeMenuOnSelect
			placeholder={placeholder}
			options={items}
		/>
	);
}
